import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import userService from '../../services/userService';
import communityService from '../../services/communityService';
import { useAuth } from '../../contexts/AuthContext';

const UserForm = ({ open, onClose, user, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'owner',
    house_number: '',
    phone: '',
    community_id: '',
  });
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCommunities = async () => {
      try {
        const response = await communityService.getAll();
        setCommunities(response.data || []);
      } catch (err) {
        console.error('Error al cargar comunidades:', err);
      }
    };

    if (open) {
      fetchCommunities();
    }
  }, [open]);

  useEffect(() => {
    if (user) {
      // Modo edición
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // No mostrar password existente
        role: user.role || 'owner',
        house_number: user.house_number || '',
        phone: user.phone || '',
        community_id: user.community_id || '',
      });
    } else {
      // Modo creación
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'owner',
        house_number: '',
        phone: '',
        community_id: currentUser?.community_id || '',
      });
    }
    setError('');
  }, [user, open, currentUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validaciones
      if (!formData.name || !formData.email) {
        setError('Nombre y email son obligatorios');
        setLoading(false);
        return;
      }

      if (!user && !formData.password) {
        setError('La contraseña es obligatoria para nuevos usuarios');
        setLoading(false);
        return;
      }

      if (formData.password && formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        setLoading(false);
        return;
      }

      // Preparar datos
      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        house_number: formData.house_number || null,
        phone: formData.phone || null,
        community_id: formData.community_id,
      };

      // Solo enviar password si se proporcionó
      if (formData.password) {
        userData.password = formData.password;
      }

      if (user) {
        // Actualizar usuario existente
        await userService.update(user.id, userData);
      } else {
        // Crear nuevo usuario
        await userService.create(userData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {user ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre Completo"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Teléfono"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
                helperText="Formato: 6000-0000 (opcional)"
                placeholder="6000-0000"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Comunidad"
                name="community_id"
                value={formData.community_id}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {communities.map((community) => (
                  <MenuItem key={community.id} value={community.id}>
                    {community.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={user ? 'Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required={!user}
                disabled={loading}
                helperText="Mínimo 6 caracteres"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Rol"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <MenuItem value="owner">Propietario</MenuItem>
                <MenuItem value="admin-temp">Administrador Temporal</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número de Casa"
                name="house_number"
                value={formData.house_number}
                onChange={handleChange}
                disabled={loading}
                helperText={formData.role === 'owner' ? 'Requerido para propietarios' : 'Opcional'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (user ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;
